import { useState, useEffect } from 'react';
import { useParams, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import ReactMarkdown from 'react-markdown';
import { Loader2 } from 'lucide-react';
import { Header } from '../components/layout/Header';
import { Footer } from '../components/layout/Footer';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Mapping des types de page vers les slugs d'URL
const PAGE_TYPE_MAP = {
  'cgv': 'cgv',
  'terms': 'cgv',
  'privacy': 'privacy',
  'cookies': 'cookies',
  'mentions-legales': 'legal-notice',
  'legal-notice': 'legal-notice'
};

export function LegalPage() {
  const { pageType: paramPageType } = useParams();
  const location = useLocation();
  const { t, i18n } = useTranslation();
  const [page, setPage] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Extraire le type de page depuis l'URL ou le paramètre
  const pathSegment = location.pathname.split('/').filter(Boolean)[0];
  const pageType = paramPageType || pathSegment;
  const apiPageType = PAGE_TYPE_MAP[pageType] || pageType;

  useEffect(() => {
    const fetchPage = async () => {
      try {
        setLoading(true);
        const response = await axios.get(`${API_URL}/api/consent/legal/${apiPageType}`);
        setPage(response.data);
        setError(null);
      } catch (err) {
        console.error('Error fetching legal page:', err);
        setError(err.response?.data?.detail || 'Page not found');
      } finally {
        setLoading(false);
      }
    };

    if (apiPageType) {
      fetchPage();
    }
  }, [apiPageType]);

  // Obtenir le contenu dans la langue actuelle
  const getLocalizedContent = (content) => {
    if (!content) return '';
    return content[i18n.language] || content.fr || content.en || '';
  };

  return (
    <div className="min-h-screen flex flex-col bg-mutagim-dark" data-testid="legal-page">
      <Header />
      
      <main className="flex-1 max-w-4xl mx-auto px-4 py-12 w-full">
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <Loader2 className="h-8 w-8 animate-spin text-mutagim-red" />
          </div>
        ) : error ? (
          <div className="text-center py-20">
            <h1 className="text-2xl font-bold text-white mb-4">Page non trouvée</h1>
            <p className="text-gray-400">{error}</p>
          </div>
        ) : page ? (
          <article className="prose prose-invert prose-red max-w-none">
            <h1 className="text-3xl md:text-4xl font-bold text-white mb-2">
              {getLocalizedContent(page.title)}
            </h1>
            
            {page.last_updated && (
              <p className="text-gray-400 text-sm mb-8">
                {t('legal.lastUpdated')}: {new Date(page.last_updated).toLocaleDateString(i18n.language)}
              </p>
            )}
            
            <div className="text-gray-300 legal-content">
              <ReactMarkdown
                components={{
                  h1: ({ children }) => <h1 className="text-2xl font-bold text-white mt-8 mb-4">{children}</h1>,
                  h2: ({ children }) => <h2 className="text-xl font-semibold text-white mt-6 mb-3">{children}</h2>,
                  h3: ({ children }) => <h3 className="text-lg font-medium text-white mt-4 mb-2">{children}</h3>,
                  p: ({ children }) => <p className="text-gray-300 mb-4 leading-relaxed">{children}</p>,
                  ul: ({ children }) => <ul className="list-disc list-inside mb-4 space-y-2 text-gray-300">{children}</ul>,
                  ol: ({ children }) => <ol className="list-decimal list-inside mb-4 space-y-2 text-gray-300">{children}</ol>,
                  li: ({ children }) => <li className="text-gray-300">{children}</li>,
                  a: ({ href, children }) => (
                    <a href={href} className="text-mutagim-red hover:underline">{children}</a>
                  ),
                  strong: ({ children }) => <strong className="text-white font-semibold">{children}</strong>,
                }}
              >
                {getLocalizedContent(page.content)}
              </ReactMarkdown>
            </div>
          </article>
        ) : null}
      </main>
      
      <Footer />
    </div>
  );
}

export default LegalPage;
